/** The second-factor branch of `POST /api/login`. */

import type { LoginMfaMethod, MethodStrength, MfaMethodType } from './domain';

export interface LoginMfaPending {
  mfaToken: string;
  methods: LoginMfaMethod[];
  /** `account-security`: present only when the server rated the methods. */
  methodStrength?: Partial<Record<MfaMethodType, MethodStrength>>;
  /** `recovery_options` as the server sent it ("Lost your phone?"); parse with `recoveryOptions`. */
  recoveryOptions?: unknown;
}

/**
 * A 401 whose body says `mfa_required` with a token is the second-factor step,
 * not a wrong password. Anything else (a string body, no token) is the old 401.
 */
export function loginMfaPending(status: number, data: unknown): LoginMfaPending | null {
  if (status !== 401 || typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body['mfa_required'] !== true || typeof body['mfa_token'] !== 'string' || !body['mfa_token']) return null;
  const methods = Array.isArray(body['methods']) ? (body['methods'] as LoginMfaMethod[]) : [];
  const pending: LoginMfaPending = { mfaToken: body['mfa_token'] as string, methods };
  const strength = body['method_strength'];
  if (strength && typeof strength === 'object' && !Array.isArray(strength)) {
    pending.methodStrength = strength as LoginMfaPending['methodStrength'];
  }
  if (Array.isArray(body['recovery_options'])) pending.recoveryOptions = body['recovery_options'];
  return pending;
}

/** The login response flags an account that must enroll before using the app. */
export function loginEnrollmentRequired(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as Record<string, unknown>)['mfa_enrollment_required'] === true;
}
